// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type {
  CitecUserClientProfileResponseBody,
  UpdateCitecUserClientProfileRequestBody,
} from '@shared/data-access/interfaces/api/brokerage/client-types';
import {
  getInversisOptionSlug,
  INVERSIS_COUNTRY_OPTIONS,
  INVERSIS_DOCUMENT_TYPE_OPTIONS,
  INVERSIS_PROVINCE_OPTIONS,
  INVERSIS_VIA_OPTIONS,
} from '../types/inversis-id-map';
import type { PersonalDataFormData } from '../types/personal-data';

/** Inversis gender codes — onboarding only (`build-client-data.ts`). */
const INVERSIS_GENDER_TO_SLUG = {
  H: 'male',
  M: 'female',
} as const satisfies Record<string, PersonalDataFormData['gender']>;

/** Inversis marital status codes — onboarding only. */
const INVERSIS_MARITAL_STATUS_TO_SLUG = {
  S: 'single',
  C: 'married',
} as const satisfies Record<string, PersonalDataFormData['marital_status']>;

type InversisOptionNamespace = 'countries' | 'vias' | 'provinces' | 'documentTypes';

const OPTION_SLUGS = {
  countries: INVERSIS_COUNTRY_OPTIONS,
  vias: INVERSIS_VIA_OPTIONS,
  provinces: INVERSIS_PROVINCE_OPTIONS,
  documentTypes: INVERSIS_DOCUMENT_TYPE_OPTIONS,
} as const;

const toFormGenderSlug = (
  value?: string | null,
): PersonalDataFormData['gender'] => {
  const trimmed = value?.trim() ?? '';

  if (!trimmed) {
    return 'male';
  }

  const inversisSlug =
    INVERSIS_GENDER_TO_SLUG[
      trimmed.toUpperCase() as keyof typeof INVERSIS_GENDER_TO_SLUG
    ];

  if (inversisSlug) {
    return inversisSlug;
  }

  const normalized = trimmed.toLowerCase();

  return normalized === 'female' || normalized === 'f' ? 'female' : 'male';
};

const toProfileGenderSlug = (
  gender: PersonalDataFormData['gender'],
): UpdateCitecUserClientProfileRequestBody['gender'] => {
  const inversisSlug =
    INVERSIS_GENDER_TO_SLUG[
      gender.toUpperCase() as keyof typeof INVERSIS_GENDER_TO_SLUG
    ];

  return inversisSlug ?? gender;
};

const toFormMaritalStatusSlug = (
  value?: string | null,
): PersonalDataFormData['marital_status'] => {
  const trimmed = value?.trim() ?? '';

  if (!trimmed) {
    return 'single';
  }

  const inversisSlug =
    INVERSIS_MARITAL_STATUS_TO_SLUG[
      trimmed.toUpperCase() as keyof typeof INVERSIS_MARITAL_STATUS_TO_SLUG
    ];

  if (inversisSlug) {
    return inversisSlug;
  }

  return trimmed.toLowerCase() === 'married' ? 'married' : 'single';
};

const toProfileMaritalStatusSlug = (
  maritalStatus: PersonalDataFormData['marital_status'],
): UpdateCitecUserClientProfileRequestBody['marital_status'] => {
  const inversisSlug =
    INVERSIS_MARITAL_STATUS_TO_SLUG[
      maritalStatus.toUpperCase() as keyof typeof INVERSIS_MARITAL_STATUS_TO_SLUG
    ];

  return inversisSlug ?? maritalStatus;
};

const toFormOptionSlug = (
  value: string | null | undefined,
  namespace: InversisOptionNamespace,
): string => {
  const trimmed = value?.trim() ?? '';

  if (!trimmed) {
    return '';
  }

  const options = OPTION_SLUGS[namespace];

  if (options.has(trimmed)) {
    return trimmed;
  }

  const slugFromInversisId = getInversisOptionSlug(trimmed, namespace);

  if (slugFromInversisId) {
    return slugFromInversisId;
  }

  const normalizedSlug = trimmed.toLowerCase().replace(/\s+/g, '-');

  if (options.has(normalizedSlug)) {
    return normalizedSlug;
  }

  return trimmed;
};

const toProfileOptionSlug = (
  value: string | undefined,
  namespace: InversisOptionNamespace,
): string | undefined => {
  const trimmed = value?.trim();

  if (!trimmed) {
    return undefined;
  }

  const options = OPTION_SLUGS[namespace];

  if (options.has(trimmed)) {
    return trimmed;
  }

  return getInversisOptionSlug(trimmed, namespace) ?? trimmed;
};

const resolveProvinceSlug = (profile: CitecUserClientProfileResponseBody): string =>
  toFormOptionSlug(profile.province ?? profile.region, 'provinces');

const hasDifferentFiscalAddress = (
  profile: CitecUserClientProfileResponseBody,
): boolean => {
  if (!profile.fiscal_street_name) {
    return false;
  }

  const province = resolveProvinceSlug(profile);

  return (
    toFormOptionSlug(profile.fiscal_country, 'countries') !==
      toFormOptionSlug(profile.country, 'countries') ||
    toFormOptionSlug(profile.fiscal_street_type, 'vias') !==
      toFormOptionSlug(profile.street_type, 'vias') ||
    profile.fiscal_street_name !== profile.street_name ||
    profile.fiscal_street_number !== profile.street_number ||
    profile.fiscal_additional_address_info !== profile.additional_address_info ||
    toFormOptionSlug(profile.fiscal_province, 'provinces') !== province
  );
};

const resolveFiscalAddressFields = (personalData: PersonalDataFormData) => {
  if (personalData.has_different_fiscal_address && personalData.fiscal_address) {
    return personalData.fiscal_address;
  }

  return personalData.postal_address;
};

/**
 * Maps a Citec client profile (GET) into personal-data form fields. Accepts an
 * immer draft; mutates `personalData` in place.
 */
export const hydratePersonalDataFromCitecClientProfile = (
  personalData: PersonalDataFormData,
  profile: CitecUserClientProfileResponseBody,
): void => {
  const differentFiscalAddress = hasDifferentFiscalAddress(profile);
  const province = resolveProvinceSlug(profile);

  personalData.first_name = profile.first_name ?? '';
  personalData.second_name = profile.second_name ?? '';
  personalData.first_surname = profile.first_surname ?? '';
  personalData.second_surname = profile.second_surname ?? '';
  personalData.birth_date = profile.birth_date ?? '';
  personalData.birth_city = profile.birth_city ?? '';
  personalData.marital_status = toFormMaritalStatusSlug(profile.marital_status);
  personalData.gender = toFormGenderSlug(profile.gender);
  personalData.nationality = toFormOptionSlug(profile.nationality, 'countries');
  personalData.document_type =
    toFormOptionSlug(profile.document_type, 'documentTypes') || 'dni-nif';
  personalData.document_number = profile.document_number ?? profile.nif_nie ?? '';
  personalData.document_expiration_date = profile.doc_expiration_date ?? '';
  personalData.document_permanent_validity = false;
  personalData.email = profile.email;
  personalData.phone_number = profile.phone_number ?? '';
  personalData.postal_address.street_type = toFormOptionSlug(
    profile.street_type,
    'vias',
  );
  personalData.postal_address.street_name =
    profile.street_name ?? profile.address ?? '';
  personalData.postal_address.street_number = profile.street_number ?? '';
  personalData.postal_address.additional_address_info =
    profile.additional_address_info ?? '';
  personalData.postal_address.province = province;
  personalData.postal_address.postal_code = profile.postal_code ?? '';
  personalData.postal_address.city = profile.city ?? '';
  personalData.postal_address.country =
    toFormOptionSlug(profile.country, 'countries') || 'spain';
  personalData.has_different_fiscal_address = differentFiscalAddress;
  personalData.fiscal_address = differentFiscalAddress
    ? {
        street_type: toFormOptionSlug(profile.fiscal_street_type, 'vias'),
        street_name: profile.fiscal_street_name ?? '',
        street_number: profile.fiscal_street_number ?? '',
        additional_address_info: profile.fiscal_additional_address_info ?? '',
        province: toFormOptionSlug(profile.fiscal_province, 'provinces'),
        postal_code: profile.postal_code ?? '',
        city: profile.city ?? '',
        country: toFormOptionSlug(profile.fiscal_country, 'countries') || 'spain',
      }
    : undefined;
};

/**
 * Maps personal-data form fields into the Citec client profile PATCH body. Always
 * emits string slugs (`female`, `spain`, …), never Inversis ids.
 */
export const buildCitecClientDataFromPersonalData = (
  personalData: PersonalDataFormData,
): UpdateCitecUserClientProfileRequestBody => {
  const fiscalAddress = resolveFiscalAddressFields(personalData);
  const province = toProfileOptionSlug(
    personalData.postal_address.province,
    'provinces',
  );

  return {
    first_name: personalData.first_name,
    second_name: personalData.second_name || undefined,
    first_surname: personalData.first_surname,
    second_surname: personalData.second_surname || undefined,
    birth_date: personalData.birth_date,
    phone_number: personalData.phone_number,
    gender: toProfileGenderSlug(personalData.gender),
    marital_status: toProfileMaritalStatusSlug(personalData.marital_status),
    birth_city: personalData.birth_city,
    nationality: toProfileOptionSlug(personalData.nationality, 'countries'),
    street_type: toProfileOptionSlug(
      personalData.postal_address.street_type,
      'vias',
    ),
    street_name: personalData.postal_address.street_name,
    street_number: personalData.postal_address.street_number,
    additional_address_info:
      personalData.postal_address.additional_address_info || undefined,
    province,
    city: personalData.postal_address.city,
    region: province,
    document_number: personalData.document_number,
    document_type: toProfileOptionSlug(personalData.document_type, 'documentTypes'),
    doc_expiration_date:
      personalData.document_permanent_validity ||
      !personalData.document_expiration_date
        ? undefined
        : personalData.document_expiration_date,
    country: toProfileOptionSlug(personalData.postal_address.country, 'countries'),
    postal_code: personalData.postal_address.postal_code,
    fiscal_country: toProfileOptionSlug(fiscalAddress.country, 'countries'),
    fiscal_street_type: toProfileOptionSlug(fiscalAddress.street_type, 'vias'),
    fiscal_street_name: fiscalAddress.street_name,
    fiscal_street_number: fiscalAddress.street_number,
    fiscal_additional_address_info:
      fiscalAddress.additional_address_info || undefined,
    fiscal_province: toProfileOptionSlug(fiscalAddress.province, 'provinces'),
  };
};
